import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
} from 'react-router-dom';

import AnalyticsDashboard from './components/analyticsDashboard/AnalyticsDashboard';
import AnalyticsDetail from './components/analyticsDetail/AnalyticsDetail';
import AuthCallback from './components/AuthCallback';
import AuthorizedRouteV6 from './components/AuthorizedRouteV6';
import BudgetDetail from './components/budgetDetail/BudgetDetail';
import BudgetsPortal from './components/budgets/BudgetsPortal';
import NoPermission from './components/common/errorPages/NoPermission';
import NotFound from './components/common/errorPages/NotFound';
import UnknownError from './components/common/errorPages/UnknownError';
import DashboardPortal from './components/dashboard/DashboardPortal';
import FormBuilder from './components/formBuilder/FormBuilder';
import FormInstance from './components/formInstance/FormInstance';
import FormRepository from './components/formRepository/FormRepository';
import InventoryPortalClassWrapper from './components/inventory/InventoryPortalClassWrapper';
import InventoryDetailClassWrapper from './components/inventoryDetail/InventoryDetailClassWrapper';
import InventoryExportPortal from './components/inventoryExport/InventoryExportPortal';
import InventoryExportDetailClassWrapper from './components/inventoryExportDetail/InventoryExportDetailClassWrapper';
import HennepinSignCodePdfReport from './components/inventoryReports/HennepinSignCodePdfReport';
import Layout from './components/Layout';
import Login from './components/login/Login';
import Registration from './components/login/Registration';
import TenantCreation from './components/login/TenantCreation';
import TenantEntry from './components/login/TenantEntry';
import TenantSelection from './components/login/TenantSelection';
import Unverified from './components/login/Unverified';
import VerifyEmail from './components/login/VerifyEmail';
import MapPortal from './components/mapPortal/MapPortal';
import NavigationProvider from './components/NavigationProvider';
import RequestsPortal from './components/requestsPortal/RequestsPortal';
import RoleDetail from './components/roleDetail/RoleDetail';
import SchedulePortal from './components/schedule/SchedulePortal';
import SchemaDetail from './components/schemaDetail/SchemaDetail';
import SchemaPortal from './components/schemaPortal/SchemaPortal';
import SearchPortal from './components/searchPortal/SearchPortal';
import TeamPortal from './components/teamPortal/TeamPortal';
import UserDetail from './components/userDetail/UserDetail';
import UserGroupDetail from './components/userGroupDetail/UserGroupDetail';
import WorkExportDetail from './components/workExportDetail/WorkExportDetail';
import WorkExports from './components/workExports/WorkExports';
import WorkDetail from './components/workOrderDetail/WorkOrderDetail';
import AldotWorkOrderPdfReport from './components/workOrderDetailPdfReport/AldotWorkOrderPdfReport/AldotWorkOrderPdfReport';
import CollinsMpcPdfReport from './components/workOrderDetailPdfReport/CollinsMpcPdfReport/CollinsMpcPdfReport';
import GwrrWorkOrderPdfReport from './components/workOrderDetailPdfReport/GwrrWorkOrderPdfReport/GwrrWorkOrderPdfReport';
import IdlWorkOrderPdfReport from './components/workOrderDetailPdfReport/IdlWorkOrderPdfReport/IdlWorkOrderPdfReport';
import OdotWorkOrderPdfReport from './components/workOrderDetailPdfReport/OdotWorkOrderPdfReport/OdotWorkOrderPdfReport';
import ScdotWorkOrderPdfReport from './components/workOrderDetailPdfReport/ScdotWorkOrderPdfReport/ScdotWorkOrderPdfReport';
import WorkOrderPdfReport from './components/workOrderDetailPdfReport/WorkOrderPdfReport/WorkOrderPdfReport';
import WorkOrders from './components/workOrders/WorkOrders';
import WorkTemplate from './components/workTemplate/WorkTemplate';
import WorkTemplates from './components/workTemplates/WorkTemplates';
import { RedirectTo } from './RedirectTo';

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        <NavigationProvider>
          <Outlet />
        </NavigationProvider>
      }
    >
      <Route element={<Layout />} errorElement={<UnknownError />}>
        <Route element={<AuthorizedRouteV6 />}>
          {/* Landing Portals */}
          <Route path="/inventory" element={<InventoryPortalClassWrapper />} />
          <Route path="/team" element={<TeamPortal />} />
          <Route path="/workOrders" element={<WorkOrders />} />
          <Route path="/analytics/dashboard" element={<AnalyticsDashboard />} />
          <Route path="/schedule" element={<SchedulePortal />} />
          <Route path="/map" element={<MapPortal />} />
          <Route path="/dashboard" element={<DashboardPortal />} />
          <Route path="/budgets" element={<BudgetsPortal />} />
          <Route path="/search" element={<SearchPortal />} />
          {/* And the rest... */}
          <Route path="/team/group/:id" element={<UserGroupDetail />} />
          <Route path="/team/role/:id" element={<RoleDetail />} />
          <Route path="/team/:id" element={<UserDetail />} />
          <Route path="/workOrders/:workOrderId" element={<WorkDetail />} />
          <Route
            path="/workOrders/:workOrderId/:taskId"
            element={<WorkDetail />}
          />
          <Route
            path="/workOrder/:workOrderId/gwrrPdfReport/:reportType"
            element={<GwrrWorkOrderPdfReport />}
          />
          <Route
            path="/workOrder/:workOrderId/aldotPdfReport"
            element={<AldotWorkOrderPdfReport />}
          />
          <Route
            path="/workOrder/:workOrderId/odotPdfReport"
            element={<OdotWorkOrderPdfReport />}
          />
          <Route
            path="/workOrder/:workOrderId/workPdfReport"
            element={<WorkOrderPdfReport />}
          />
          <Route
            path="/workOrder/:workOrderId/scdotPdfReport"
            element={<ScdotWorkOrderPdfReport />}
          />
          <Route
            path="/workOrder/:workOrderId/idlPdfReport"
            element={<IdlWorkOrderPdfReport />}
          />
          <Route
            path="/workOrder/:workOrderId/collinsMpcPdfReport"
            element={<CollinsMpcPdfReport />}
          />
          <Route
            path="/inventory/MUTCD"
            element={<HennepinSignCodePdfReport />}
          />
          <Route
            path="/inventory/:id"
            element={<InventoryDetailClassWrapper />}
          />
          <Route
            path="/inventory/export/:id"
            element={<InventoryExportDetailClassWrapper />}
          />
          <Route path="/inventory/export" element={<InventoryExportPortal />} />
          <Route path="/workExports" element={<WorkExports />} />
          <Route path="/workExports/:id" element={<WorkExportDetail />} />
          <Route path="/work-templates" element={<WorkTemplates />} />
          <Route path="/work-templates/:id" element={<WorkTemplate />} />
          <Route path="/form-repository" element={<FormRepository />} />
          <Route path="/form-builder/:id/preview" element={<FormInstance />} />
          <Route path="/form-instance/:id" element={<FormInstance />} />
          <Route path="/form-builder/:id" element={<FormBuilder />} />
          <Route path="/analytics/:id" element={<AnalyticsDetail />} />
          <Route path="/budget" element={<BudgetDetail />}>
            <Route path=":id" element={<BudgetDetail />}>
              <Route path=":unitId" element={<BudgetDetail />} />
            </Route>
          </Route>
          <Route path="/budget/:id" element={<BudgetDetail />} />
          <Route path="/inventory-requests" element={<RequestsPortal />} />
          <Route path="/no-permission" element={<NoPermission />} />
          <Route path="/not-found" element={<NotFound />} />
          <Route path="/unknown-error" element={<UnknownError />} />
          <Route path="/inventoryTypes/:id" element={<SchemaDetail />} />
          <Route path="/inventoryTypes" element={<SchemaPortal />} />
          <Route path="/" element={<div />} />
        </Route>
      </Route>
      <Route
        path="/admin/inventoryTypes/:id"
        element={<RedirectTo destination="/inventoryTypes/:id" />}
      />
      <Route
        path="/admin/inventoryTypes"
        element={<RedirectTo destination="/inventoryTypes" />}
      />
      <Route path="/login" element={<Login />} />
      <Route path="/callback/:token" element={<AuthCallback />} />
      <Route path="/registration" element={<Registration />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/tenant-creation" element={<TenantCreation />} />
      <Route path="/tenant-selection" element={<TenantSelection />} />
      <Route path="/tenant-entry" element={<TenantEntry />} />
      <Route path="/unverified" element={<Unverified />} />
      <Route path="*" element={<RedirectTo destination="/unknown-error" />} />
    </Route>,
  ),
);
